import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import GroupsIcon from "@mui/icons-material/Groups";
import WalletIcon from "@mui/icons-material/Wallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import Swal from "sweetalert2";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import CustomDateRangePicker from "../../../Components/CustomDateRangePicker/CustomDateRangePicker";
import { AUTH_LOGIN } from "../../../Config/firebase";
import DashboardKpiCard from "./components/DashboardKpiCard";
import DashboardChartCard from "./components/DashboardChartCard";
import MonthlyPerformanceChart from "./components/MonthlyPerformanceChart";
import ClientSplitChart from "./components/ClientSplitChart";
import SegmentDashboard from "./components/SegmentDashboard";
import useDashboardData, { DASHBOARD_PERIODS } from "./hooks/useDashboardData";
import { USER_SEGMENTS } from "./utils/segments";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);
const formatPercent2 = (value) => `${Number(value || 0).toFixed(2)}%`;

const hasChartData = (rows, keys) =>
  Array.isArray(rows) && rows.some((row) => keys.some((key) => Math.abs(Number(row[key] || 0)) > 0));

const periodOptions = [
  { label: "All time", value: DASHBOARD_PERIODS.ALL_TIME },
  { label: "Last month", value: DASHBOARD_PERIODS.LAST_MONTH },
  { label: "Last 12 months", value: DASHBOARD_PERIODS.LAST_12_MONTHS },
  { label: "Last 24 months", value: DASHBOARD_PERIODS.LAST_24_MONTHS },
  { label: "YTD", value: DASHBOARD_PERIODS.YTD },
  { label: "Custom range", value: DASHBOARD_PERIODS.CUSTOM },
];

const DashboardRedesign = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 960px)");
  const userInfo = JSON.parse(localStorage.getItem("fiddux_info")) || null;
  const [period, setPeriod] = React.useState(DASHBOARD_PERIODS.LAST_12_MONTHS);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const { isLoading, error, company, bySegment, lastUpdated } = useDashboardData({
    period,
    startDate,
    endDate,
  });

  const periodLabel = periodOptions.find((option) => option.value === period)?.label || "";
  const activeSegment = USER_SEGMENTS[activeTab] || USER_SEGMENTS[0];

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogoutClick = () => {
    setAnchorEl(null);
    Swal.fire({
      title: "Are you sure you want to logout?",
      text: "Logging out will end your session and you will be redirected to the login page.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await signOut(AUTH_LOGIN);
        localStorage.clear();
        Swal.fire({
          title: "Logged Out",
          text: "You have been successfully logged out. Redirecting to login page...",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          navigate("/login");
        });
      }
    });
  };

  const updatedLabel = lastUpdated
    ? `Updated ${lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
    : "Loading live Firestore data";

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          paddingTop: { xs: "80px", md: 0 },
          p: { xs: 2, md: 0 },
        }}
      >
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", md: "28px" }, fontWeight: 800, color: "#fff" }}>
            Dashboard
          </Typography>
          <Typography sx={{ color: "#aaa", fontSize: "14px", mt: 0.5 }}>
            Company overview and per client-type breakdown
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ color: "#fff", fontSize: "16px", fontWeight: 700 }}>Welcome Admin</Typography>
            <Typography sx={{ color: "#aaa", fontSize: "12px" }}>{updatedLabel}</Typography>
          </Box>
          <IconButton size="small" sx={{ ml: 0.5 }} aria-haspopup="true" onClick={handleMenuClick}>
            <Avatar sx={{ width: 45, height: 45 }}>A</Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <Box sx={{ padding: 2, width: 220 }}>
              <Typography sx={{ fontSize: "16px", color: "#555", wordBreak: "break-word" }}>
                {userInfo?.email}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
            <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1, md: 0 }, mt: { xs: 0, md: 2 } }}>
        {/* Filtro de período */}
        <Card
          sx={{
            mb: 2,
            background: "linear-gradient(135deg, #353535 0%, #1e1e1e 100%)",
            border: "1px solid rgba(42, 245, 152, 0.18)",
            borderRadius: "12px",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box>
                <Typography sx={{ color: "#fff", fontSize: "16px", fontWeight: 700 }}>
                  Live executive filters
                </Typography>
                <Typography sx={{ color: "#aaa", fontSize: "13px", mt: 0.5 }}>
                  Read-only. Deposits use approved status; withdrawals use paid status. AUM and client
                  counts are always current.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                {periodOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={period === option.value ? "contained" : "outlined"}
                    onClick={() => setPeriod(option.value)}
                    sx={{
                      textTransform: "none",
                      borderColor: "rgba(42, 245, 152, 0.4)",
                      color: period === option.value ? "#000" : "#fff",
                      backgroundColor: period === option.value ? "#2AF598" : "transparent",
                      "&:hover": {
                        borderColor: "#2AF598",
                        backgroundColor: period === option.value ? "#2AF598" : "rgba(42, 245, 152, 0.08)",
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
            </Box>
            {period === DASHBOARD_PERIODS.CUSTOM && (
              <Box sx={{ mt: 2 }}>
                <CustomDateRangePicker
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                />
              </Box>
            )}
            {error && (
              <Box sx={{ mt: 2, p: 1.5, border: "1px solid rgba(245, 42, 45, 0.35)", borderRadius: "8px" }}>
                <Typography sx={{ color: "#F52A2D", fontSize: "13px" }}>
                  Data unavailable for one or more dashboard sections. Existing values are not changed.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Company snapshot strip */}
        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Total AUM"
              value={formatCurrency(company.totalAum)}
              caption="All client types · current balance"
              accentColor="#2AF598"
              borderColor="rgba(42, 245, 152, 0.2)"
              icon={<WalletIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Fund Performance"
              value={formatPercent2(company.fundPerformance)}
              caption={`${periodLabel} · whole fund (${company.monthlyPerformance.length} mo)`}
              accentColor="#3A87DE"
              borderColor="rgba(58, 135, 222, 0.2)"
              icon={<AssessmentIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Total Net Flow"
              value={formatCurrency(company.totalNetFlow)}
              caption={`${formatCurrency(company.totalDeposits)} in / ${formatCurrency(
                company.totalWithdrawals
              )} out · ${periodLabel}`}
              accentColor="#00BA2D"
              borderColor="rgba(0, 186, 45, 0.2)"
              icon={<SwapVertIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Active Clients"
              value={formatNumber(company.totalClients)}
              caption="All client types · active"
              accentColor="#FFC107"
              borderColor="rgba(255, 193, 7, 0.2)"
              icon={<GroupsIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={8}>
            <DashboardChartCard
              title="Monthly Fund Performance"
              subtitle={`Real monthly percentages (whole fund) · ${periodLabel}`}
              isLoading={isLoading}
              isEmpty={!hasChartData(company.monthlyPerformance, ["amount"])}
            >
              <MonthlyPerformanceChart data={company.monthlyPerformance} />
            </DashboardChartCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <DashboardChartCard
              title="AUM by Client Type"
              subtitle="Current AUM split across client types."
              isLoading={isLoading}
              isEmpty={!company.segmentsAum?.some((item) => item.aum > 0)}
            >
              <ClientSplitChart segments={company.segmentsAum} />
            </DashboardChartCard>
          </Grid>
        </Grid>

        {/* Abas por tipo de cliente */}
        <Card
          sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "12px",
          }}
        >
          <Box sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)", px: { xs: 1, md: 2 } }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  color: "#aaa",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "14px", md: "16px" },
                },
                "& .Mui-selected": { color: "#fff !important" },
                "& .MuiTabs-indicator": { backgroundColor: activeSegment.accent, height: 3 },
              }}
            >
              {USER_SEGMENTS.map((segment) => (
                <Tab key={segment.key} label={`${segment.label} Users`} />
              ))}
            </Tabs>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <SegmentDashboard
              segment={activeSegment}
              metrics={bySegment[activeSegment.key]}
              isLoading={isLoading}
              periodLabel={periodLabel}
            />
          </CardContent>
        </Card>

        {isMobile && (
          <Typography sx={{ color: "#aaa", fontSize: "12px", mt: 2, textAlign: "center" }}>
            Charts use the same live read-only data as desktop.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DashboardRedesign;
